import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  Clock,
  CreditCard,
  Landmark,
  MapPin,
  MessageCircle,
  Search,
  ShieldCheck,
  Smartphone,
  Users,
  UtensilsCrossed,
  Wallet,
  XCircle,
} from "lucide-react";
import API from "../api/axios";
import { useSelector } from "react-redux";
import { useShowToast } from "../store/hooks";
import { AnalyticsEvents, track } from "../utils/analytics";
import { SERVICE_DETAILS, formatCurrency, formatDate, formatTimeRange12, formatIsoToIstDay } from "../utils/constants";

const WINDOW_MS = 5 * 60 * 1000; // 5-minute payment window
const POLL_MS = 8000;
const REDIRECT_S = 6;
const RING = 2 * Math.PI * 54;

const METHODS = [
  { id: "upi", label: "UPI", desc: "GPay · PhonePe · Paytm", icon: Smartphone, tag: "Recommended" },
  { id: "card", label: "Card", desc: "Credit / Debit", icon: CreditCard },
  { id: "netbanking", label: "Net Banking", desc: "All major banks", icon: Landmark },
  { id: "wallet", label: "Wallet", desc: "Paytm · Amazon Pay", icon: Wallet },
];
const HIDE_METHODS = {
  upi: ["card", "netbanking", "wallet", "emi", "paylater", "cardless_emi"],
  card: ["upi", "netbanking", "wallet", "emi", "paylater", "cardless_emi"],
  netbanking: ["upi", "card", "wallet", "emi", "paylater", "cardless_emi"],
  wallet: ["upi", "card", "netbanking", "emi", "paylater", "cardless_emi"],
};

let razorpayScriptPromise = null;
const loadRazorpay = () => {
  if (typeof window !== "undefined" && window.Razorpay) return Promise.resolve(true);
  if (!razorpayScriptPromise) {
    razorpayScriptPromise = new Promise((resolve) => {
      const s = document.createElement("script");
      s.src = "https://checkout.razorpay.com/v1/checkout.js";
      s.async = true;
      s.onload = () => resolve(true);
      s.onerror = () => {
        razorpayScriptPromise = null;
        resolve(false);
      };
      document.body.appendChild(s);
    });
  }
  return razorpayScriptPromise;
};

const BookingPayment = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const showToast = useShowToast();
  const user = useSelector((s) => s.auth.user);

  const [booking, setBooking] = useState(null);
  const [phase, setPhase] = useState("loading"); // loading | pay | processing | success | expired | error
  const [method, setMethod] = useState("upi");
  const [now, setNow] = useState(Date.now());
  const [redirectIn, setRedirectIn] = useState(REDIRECT_S);
  const [cookWaUrl, setCookWaUrl] = useState(null);
  const [selfWaUrl, setSelfWaUrl] = useState(null);
  const [waAuto, setWaAuto] = useState(false);
  useEffect(() => {
    let alive = true;
    API.get("/whatsapp/status")
      .then((res) => {
        if (alive && res.data?.enabled) setWaAuto(true);
      })
      .catch(() => {
      });
    return () => {
      alive = false;
    };
  }, []);
  const handledRef = useRef(false);
  const aliveRef = useRef(true);
  const pollRef = useRef(null);
  const completedRef = useRef(false);
  const failedSilentDismissRef = useRef(false);
  const couponToastRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const res = await API.get(`/bookings/${bookingId}`);
      if (!aliveRef.current || handledRef.current) return;
      const b = res.data;
      setBooking(b);
      if (!couponToastRef.current && b.couponCode && Number(b.discount) > 0) {
        couponToastRef.current = true;
        showToast(`🎉 Coupon ${b.couponCode} applied — you saved ${formatCurrency(b.discount)}!`, "success");
      }
      const st = b.status;
      const paid = b.payment?.status === "paid";
      if (["confirmed", "in_progress", "completed"].includes(st) || paid) {
        handledRef.current = true;
        setPhase("success");
        setTimeout(() => {
          if (aliveRef.current) navigate(`/bookings/${bookingId}`, { replace: true });
        }, 1800);
      } else if (["expired", "cancelled", "rejected"].includes(st)) {
        handledRef.current = true;
        setPhase("expired");
      } else if (st === "requested") {
        handledRef.current = true;
        navigate(`/bookings/${bookingId}/wait`, { replace: true });
      } else {
        setPhase((p) => (p === "processing" ? p : "pay"));
      }
    } catch (err) {
      if (!aliveRef.current) return;
      if (err.response?.status === 404) {
        handledRef.current = true;
        setPhase("error");
      }
    }
  }, [bookingId, navigate, showToast]);

  useEffect(() => {
    aliveRef.current = true;
    load();
    const startPoll = () => {
      stopPoll();
      pollRef.current = setInterval(() => {
        if (!document.hidden) load();
      }, POLL_MS);
    };
    const stopPoll = () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
    const onVis = () => {
      if (document.hidden) stopPoll();
      else {
        load();
        startPoll();
      }
    };
    startPoll();
    document.addEventListener("visibilitychange", onVis);
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => {
      aliveRef.current = false;
      stopPoll();
      document.removeEventListener("visibilitychange", onVis);
      clearInterval(tick);
    };
  }, [load]);

  useEffect(() => {
    if (phase !== "expired") return undefined;
    setRedirectIn(REDIRECT_S);
    const iv = setInterval(() => {
      setRedirectIn((s) => {
        if (s <= 1) {
          clearInterval(iv);
          navigate("/cook-on-demand", { replace: true });
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(iv);
  }, [phase, navigate]);

  // UX guard only — not security.
  const payingRef = useRef(false);
  const payNow = async () => {
    if (payingRef.current || phase === "processing") return;
    payingRef.current = true;
    try {
      await payNowInner();
    } finally {
      payingRef.current = false;
    }
  };
  const payNowInner = async () => {
    if (phase === "processing") return;
    setPhase("processing");
    completedRef.current = false;
    failedSilentDismissRef.current = false;
    try {
      const msLeft = expiresAtMsForPay();
      if (msLeft != null && msLeft < 30000) {
        setPhase("pay");
        showToast("Payment window almost over — please rebook to get a fresh window.", "error");
        return;
      }
      const cookId = booking?.cook?._id || booking?.cook;
      let order;
      try {
        const orderDate = formatIsoToIstDay(booking?.date) || booking?.date;
        const res = await API.post("/payments/order", {
          cook: cookId,
          date: orderDate,
          startTime: booking?.startTime,
          endTime: booking?.endTime,
          durationHours: booking?.durationHours,
          bookingId,
        });
        order = res.data;
      } catch (err) {
        if (err.response?.status === 503) {
          handledRef.current = false;
          setPhase("pay");
          showToast(
            "Online payment is temporarily unavailable. Please try again later — no money was charged.",
            "error"
          );
          return;
        }
        setPhase("pay");
        showToast(
          err.response?.data?.message || "Could not start payment. Please try again.",
          "error"
        );
        return;
      }
      if (order?.free || order?.amountPaise === 0 || Number(order?.amountPaise || 0) <= 0) {
        const res = await API.patch(`/bookings/${bookingId}/pay`, { method, payment: null });
        completedRef.current = true;
        setBooking((prev) => ({ ...(prev || {}), ...(res.data || {}) }));
        setCookWaUrl(res.data?.cookWhatsappUrl || null);
        setSelfWaUrl(res.data?.customerWhatsappUrl || null);
        setPhase("success");
        track(AnalyticsEvents.PAYMENT_SUCCESS, {
          booking_id: String(bookingId || ""),
          amount: 0,
          method: "discount",
        });
        showToast("Discount covered the full fee — booking confirmed!", "success");
        setTimeout(() => {
          if (aliveRef.current) navigate(`/bookings/${bookingId}`, { replace: true });
        }, 6000);
        return;
      }
      const loaded = await loadRazorpay();
      if (!loaded || !window.Razorpay) {
        setPhase("pay");
        showToast("Couldn't load the payment gateway. Check your connection and retry.", "error");
        return;
      }
      handledRef.current = true;
      const rzp = new window.Razorpay({
        key: order.keyId,
        amount: order.amountPaise,
        currency: order.currency || "INR",
        order_id: order.orderId,
        name: "CookMitra",
        description: `Cook booking ${formatDate(booking?.date)} ${formatTimeRange12(booking?.startTime, booking?.endTime, "-")}`,
        prefill: { contact: user?.phone || "", email: user?.email || "" },
        theme: { color: "#e8590c" },
        ...(HIDE_METHODS[method]
          ? { config: { display: { hide: HIDE_METHODS[method].map((m) => ({ method: m })) } } }
          : {}),
        handler: async (resp) => {
          try {
            const res = await API.patch(`/bookings/${bookingId}/pay`, {
              method,
              payment: {
                razorpayOrderId: resp.razorpay_order_id,
                razorpayPaymentId: resp.razorpay_payment_id,
                razorpaySignature: resp.razorpay_signature,
              },
            });
            completedRef.current = true;
            setBooking((prev) => ({ ...(prev || {}), ...(res.data || {}) }));
            setCookWaUrl(res.data?.cookWhatsappUrl || null);
            setSelfWaUrl(res.data?.customerWhatsappUrl || null);
            setPhase("success");
            track(AnalyticsEvents.PAYMENT_SUCCESS, {
              booking_id: String(bookingId || ""),
              amount: Number(booking?.amount) || 0,
              method,
            });
            showToast("Payment successful — booking confirmed!", "success");
            setTimeout(() => {
              if (aliveRef.current) navigate(`/bookings/${bookingId}`, { replace: true });
            }, 6000);
          } catch (err) {
            completedRef.current = false;
            if (err.response?.status === 410) {
              setPhase("expired");
              showToast(err.response?.data?.message || "Payment window expired — slot released.", "error");
            } else if (err.response?.status === 409) {
              if (err.response?.data?.alreadyPaid || err.response?.data?.status === "confirmed") {
                completedRef.current = true;
                setBooking((prev) => ({ ...(prev || {}), ...(err.response?.data || {}) }));
                setPhase("success");
                showToast("Payment already recorded — booking confirmed!", "success");
              } else {
                handledRef.current = false;
                setPhase("pay");
                showToast(err.response?.data?.message || "This slot was just taken — please pick another time.", "error");
              }
            } else {
              handledRef.current = false;
              setPhase("pay");
              showToast(err.response?.data?.message || "Payment verification failed — please try again.", "error");
            }
          }
        },
        modal: {
          ondismiss: () => {
            if (completedRef.current) return;
            if (failedSilentDismissRef.current) {
              failedSilentDismissRef.current = false;
              return;
            }
            handledRef.current = false;
            setPhase("pay");
            showToast("Payment cancelled — your slot is still held until the timer ends.", "info");
          },
        },
      });
      rzp.on("payment.failed", () => {
        if (completedRef.current) return;
        failedSilentDismissRef.current = true;
        handledRef.current = false;
        setPhase("pay");
        showToast("Payment failed — no money was charged. Please try again.", "error");
      });
      rzp.open();
    } catch (err) {
      handledRef.current = false;
      setPhase("pay");
      showToast(err.response?.data?.message || "Payment failed — please try again.", "error");
    }
  };

  const expiresAtMsForPay = () => {
    const acceptedEntry = (booking?.statusHistory || []).find((s) => s.status === "accepted");
    const acceptedMs = acceptedEntry?.timestamp ? new Date(acceptedEntry.timestamp).getTime() : null;
    const exp = booking?.paymentExpiresAt
      ? new Date(booking.paymentExpiresAt).getTime()
      : acceptedMs
        ? acceptedMs + WINDOW_MS
        : null;
    return exp ? Math.max(0, exp - Date.now()) : null;
  };

  const acceptedEntry = (booking?.statusHistory || []).find((s) => s.status === "accepted");
  const acceptedMs = acceptedEntry?.timestamp ? new Date(acceptedEntry.timestamp).getTime() : null;
  const expiresAtMs = booking?.paymentExpiresAt
    ? new Date(booking.paymentExpiresAt).getTime()
    : acceptedMs
      ? acceptedMs + WINDOW_MS
      : null;
  const remainingMs = expiresAtMs ? Math.max(0, expiresAtMs - now) : WINDOW_MS;
  const secondsLeft = Math.ceil(remainingMs / 1000);
  const clockText = `${String(Math.floor(secondsLeft / 60)).padStart(2, "0")}:${String(
    secondsLeft % 60
  ).padStart(2, "0")}`;
  const progress = Math.max(0, Math.min(1, remainingMs / WINDOW_MS));
  const service = SERVICE_DETAILS[booking?.serviceType] || {};
  const cookName = booking?.cook?.name || "your cook";

  if (phase === "loading") {
    return (
      <div className="booking-flow-page">
        <div className="bf-loader" role="status" aria-label="Loading booking">
          <div className="spinner" aria-hidden="true" />
          <p>Loading your payment…</p>
        </div>
      </div>
    );
  }

  if (phase === "error") {
    return (
      <div className="booking-flow-page">
        <div className="bf-card bf-center">
          <XCircle className="bf-sorry-icon" size={56} />
          <h1 className="bf-title">Booking not found</h1>
          <p className="bf-sub">We couldn't find this booking. It may have been removed.</p>
          <button className="btn btn-primary bf-btn" onClick={() => navigate("/cook-on-demand")}>
            <Search size={18} /> Browse cooks
          </button>
        </div>
      </div>
    );
  }

  if (phase === "expired") {
    return (
      <div className="booking-flow-page">
        <div className="bf-card bf-center">
          <XCircle className="bf-sorry-icon" size={64} />
          <h1 className="bf-title">Payment window expired</h1>
          <p className="bf-sub">
            The 5-minute window closed, so {cookName}'s slot has been released for other customers.
            No money was charged.
          </p>
          <div className="bf-redirect">
            Taking you to available cooks in <b>{redirectIn}s</b>…
          </div>
          <button className="btn btn-primary bf-btn" onClick={() => navigate("/cook-on-demand", { replace: true })}>
            <Search size={18} /> Find a cook again <ArrowRight size={18} />
          </button>
        </div>
      </div>
    );
  }

  if (phase === "success") {
    return (
      <div className="booking-flow-page">
        <div className="bf-card bf-center">
          <div className="bf-burst">
            <CheckCircle2 size={52} />
          </div>
          <h1 className="bf-title">Booking confirmed! 🎉</h1>
          <p className="bf-sub">{cookName} will arrive as scheduled. Redirecting to your booking…</p>
          {(cookWaUrl || selfWaUrl) && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.5rem",
                marginTop: "0.75rem",
                width: "100%",
                maxWidth: 360,
              }}
            >
              <p className="bf-sub" style={{ margin: 0 }}>
                One tap to share over WhatsApp:
              </p>
              {cookWaUrl && (
                <a href={cookWaUrl} target="_blank" rel="noreferrer" className="btn btn-success bf-btn">
                  <MessageCircle size={18} /> Send details to cook
                </a>
              )}
              {selfWaUrl && (
                <a href={selfWaUrl} target="_blank" rel="noreferrer" className="btn btn-outline bf-btn">
                  <MessageCircle size={18} /> Send confirmation to my WhatsApp
                </a>
              )}
              {waAuto && (
                <p className="bf-sub" style={{ margin: 0 }}>
                  Booking updates will also arrive automatically on your WhatsApp.
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  const items = Array.isArray(booking?.selectedItems) ? booking.selectedItems : [];
  const amount = Number(booking?.amount || 0);
  const lowTime = secondsLeft <= 60;

  return (
    <div className="booking-flow-page">
      <div className="bf-card bf-pay-card">
        <div className="bf-wait-head">
          <div className="bf-ring-wrap small">
            <svg className="bf-ring" viewBox="0 0 120 120" width="104" height="104">
              <circle className="bf-ring-track" cx="60" cy="60" r="54" />
              <circle
                className={`bf-ring-fill${lowTime ? " warn" : ""}`}
                cx="60"
                cy="60"
                r="54"
                strokeDasharray={RING}
                strokeDashoffset={RING * (1 - progress)}
              />
            </svg>
            <div className="bf-ring-center">
              <Clock size={14} />
              <span className={`bf-ring-time${lowTime ? " warn" : ""}`}>{clockText}</span>
              <span className="bf-ring-label">to pay</span>
            </div>
          </div>
          <div className="bf-wait-copy">
            <h1 className="bf-title">Confirm your booking</h1>
          </div>
        </div>

        <div className="bf-grid">
          <div className="bf-grid-main">
            <div className="bf-summary">
              <span className="bf-chip"><UtensilsCrossed size={14} /> {service.label || "Home cooking"}</span>
              <span className="bf-chip">📅 {formatDate(booking?.date)}</span>
              <span className="bf-chip">⏰ {formatTimeRange12(booking?.startTime, booking?.endTime)}</span>
              {booking?.guests ? <span className="bf-chip"><Users size={14} /> {booking.guests} guests</span> : null}
            </div>

            {booking?.address ? (
              <div className="bf-address"><MapPin size={15} /> {booking.address}</div>
            ) : null}

            <h3 className="bf-section-title">Choose payment method</h3>
            <div className="bf-methods">
              {METHODS.map((m) => {
                const Icon = m.icon;
                const active = method === m.id;
                return (
                  <button
                    key={m.id}
                    type="button"
                    className={`bf-method${active ? " selected" : ""}`}
                    onClick={() => setMethod(m.id)}
                    aria-pressed={active}
                  >
                    <Icon size={20} />
                    <span className="bf-method-name">{m.label}</span>
                    <span className="bf-method-desc">{m.desc}</span>
                    {m.tag ? <span className="bf-method-tag">{m.tag}</span> : null}
                  </button>
              );
            })}
            </div>
          </div>

          <aside className="bf-grid-side">
            <div className="bf-amount-box">
              <span className="bf-amount-label">Amount payable</span>
              {Number(booking?.slabPrice) > 0 ? (
                <div className="price-rows" style={{ width: "100%", textAlign: "left", marginBottom: "0.4rem" }}>
                  <div className="price-row">
                    <span>Service Price · {booking?.durationHours} hr{Number(booking?.durationHours) === 1 ? "" : "s"}</span>
                    <span>{formatCurrency(booking.slabPrice)}</span>
                  </div>
                  {booking?.couponCode ? (
                    <div className="price-row discount">
                      <span>Coupon {booking.couponCode}</span>
                      <span>−{formatCurrency(booking.discount)}</span>
                    </div>
                  ) : null}
                </div>
              ) : null}
              <span className="bf-amount">{formatCurrency(amount)}</span>
              {items.length ? <span className="bf-amount-note">{items.join(" • ")}</span> : null}
            </div>
            <button
              className="btn btn-primary bf-pay-btn"
              onClick={payNow}
              disabled={phase === "processing"}
              aria-busy={phase === "processing"}
            >
              {phase === "processing" ? "Starting payment…" : `Pay ${formatCurrency(amount)} & Confirm`}
            </button>
            <p className="bf-consent">
              By paying you agree to our <Link to="/terms">Terms</Link>,{" "}
              <Link to="/refunds">Refund Policy</Link> and{" "}
              <Link to="/customer-cancellation-refund-policy">Cancellation Policy</Link>. Payments are processed
              securely by Razorpay.
            </p>
            <div className="bf-secure"><ShieldCheck size={14} /> 256-bit encrypted • Instant confirmation</div>
            {lowTime ? <div className="bf-low-time">Hurry — the slot releases automatically at 00:00!</div> : null}
          </aside>
        </div>
      </div>

      {phase === "processing" && (
        <div className="bf-processing" role="alert">
          <div className="bf-pan"><span className="bf-pan-handle" /></div>
          <p>Processing payment<span className="bf-dots"><i>.</i><i>.</i><i>.</i></span></p>
        </div>
      )}
    </div>
  );
};

export default BookingPayment;
